import { useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { SectionHeader, EmptyState, ErrorState, Spinner } from '../../../components/ui';
import { toast, useSessionStore } from '../../../store';
import { useStrategies, useManualTradingDeployments, useCreateManualTradingWatchlistRow, usePrepareManualTradingOrder, useConfirmManualTradingOrder } from '../../../api/hooks';
import { MANUAL_STRATEGIES } from './mockData';
import ManualStrategyCard from './ManualStrategyCard';
import ConfirmManualSignalModal from './modals/ConfirmManualSignalModal';
import ExitPositionModal from './modals/ExitPositionModal';
import CancelOrderModal from './modals/CancelOrderModal';
import SymbolPickerModal from '../../../components/SymbolPickerModal';
import { friendlySegment } from './manualLabels';
import { classifyManualSignalResult, extractOrderErrorMessage, normalizeMode } from './utils/manualSignalResult';

// Dev-only escape hatch: when true AND the backend returns no deployments, show
// the local mock cards. MUST stay false — empty must mean empty in real use.
const USE_MANUAL_TRADING_MOCK_FALLBACK = false;

const titleCase = (s) => {
  const t = String(s || '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
};

// Map a backend Manual deployment → the card shape the existing UI components expect.
// Watchlist/orders/positions are local-only for now (no persistence yet) → empty arrays.
function mapDeployment(d, strategyMode, strategyModeKnown) {
  const mt = d.manual_trading || {};
  const execMode = String(d.execution_mode || '').toUpperCase(); // LIVE | PAPER
  const tradingMode =
    titleCase(d.deployment_type) || titleCase(d.global_rules?.trading_mode) || 'Intraday';
  return {
    deploymentId:   d.deployment_id,
    strategyId:     d.strategy_id,
    strategyName:   d.strategy_name || d.strategy_meta?.name || `Strategy ${d.strategy_id}`,
    strategyMode:   strategyMode || null,
    strategyModeKnown,
    deploymentRole: String(d.deployment_role || '').trim().toUpperCase() || null,
    parentDeploymentId: d.parent_deployment_id ?? null,
    status:         execMode || String(d.deployment_status || '').toUpperCase(), // LIVE/PAPER for the badge
    deploymentStatus: d.deployment_status,
    executionMode:  d.execution_mode,
    tradingMode,
    riskUnit:       'PERCENT',
    signalCount:    0,
    strategy_source: 'MANUAL',
    category:        'MANUAL_TRADING',
    strategy_meta:   d.strategy_meta || {},
    manual_trading:  mt,
    execution:       d.execution || {},
    global_rules:    d.global_rules || null,
    // local-only UI state (no backend persistence in this phase)
    symbols:   [],
    orders:    [],
    positions: [],
  };
}

export default function ManualTradingPage() {
  const sessionRole = useSessionStore((s) => s.role);
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const rawRoles = useSessionStore((s) => s.user?.roles || []);
  const canManageFanout = isSuperAdmin
    || ['operator', 'admin', 'both'].includes(sessionRole)
    || (Array.isArray(rawRoles) && rawRoles.some((role) => ['operator', 'admin', 'super_admin'].includes(role)));
  const canExecuteFanout = ['operator', 'both'].includes(sessionRole)
    || (Array.isArray(rawRoles) && rawRoles.some((role) => String(role || '').toLowerCase() === 'operator'));

  // Real data: deployed Manual Strategies. Local `strategies` state holds the
  // mapped cards plus local-only UI mutations (watchlist rows / mock signals)
  // that aren't persisted yet — re-seeded from the query while preserving those
  // local rows per deployment across refetches.
  const depQ = useManualTradingDeployments();
  // Deployment responses intentionally omit strategy_mode. Merge the existing caller-scoped
  // strategy list by strategy_id and fail closed until that mode is known.
  const strategiesQ = useStrategies();
  const strategyModes = useMemo(() => new Map(
    (strategiesQ.data || []).map((row) => [Number(row.id), String(row.strategy_mode || 'STANDARD').toUpperCase()]),
  ), [strategiesQ.data]);
  const allManualDeployments = useMemo(() => {
    const rows = (depQ.data || []).map((deployment) => {
      const strategyId = Number(deployment.strategy_id);
      return mapDeployment(
        deployment,
        strategyModes.get(strategyId),
        strategiesQ.isSuccess && strategyModes.has(strategyId),
      );
    });
    if (USE_MANUAL_TRADING_MOCK_FALLBACK && depQ.isFetched && rows.length === 0) return MANUAL_STRATEGIES;
    return rows;
  }, [depQ.data, depQ.isFetched, strategiesQ.isSuccess, strategyModes]);
  const visibleManualDeployments = useMemo(
    () => allManualDeployments.filter((deployment) => (
      String(deployment.deploymentRole || '').toUpperCase() !== 'FANOUT_CHILD'
    )),
    [allManualDeployments],
  );

  // Ephemeral, local-only BUY/SELL overlay for watchlist rows (not persisted):
  //   { `${deploymentId}:${rowId}`: { lastSignal, lastTrigger } }
  const [signalOverrides, setSignalOverrides] = useState({});
  const createWlRow = useCreateManualTradingWatchlistRow();
  const prepareOrder = usePrepareManualTradingOrder();     // prepare step
  const confirmOrder = useConfirmManualTradingOrder();      // confirm step
  // Synchronous re-entry guards (paired with the mutations' isPending) to block duplicate prepare/confirm.
  const preparingRef = useRef(false);
  const confirmingRef = useRef(false);
  const expiredRef = useRef(false); // ensures a single onExpire even if the timer fires twice

  const [collapsed, setCollapsed] = useState({}); // { [deploymentId]: true }

  // Modal context state (null = closed). Each modal mounts only when open.
  const [signalCtx, setSignalCtx] = useState(null);   // { strategy, symbol, side }
  const [signalSubmitting, setSignalSubmitting] = useState(false);
  const [signalError, setSignalError] = useState(null);
  const [exitCtx, setExitCtx] = useState(null);        // { strategy, position }
  const [addCtx, setAddCtx] = useState(null);          // strategy being added to (or null)
  const [cancelCtx, setCancelCtx] = useState(null);    // { strategy, order }

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const refresh = () => {
    depQ.refetch();
    toast.info('Refreshing deployed manual strategies…');
  };

  // BUY/SELL click. PAPER and LIVE both use the two-step prepare/confirm flow: call /prepare first and
  // open the modal ONLY with prepared values.
  const openSignal = (strategy, symbol, side) => {
    setSignalError(null);
    setSignalSubmitting(false);
    const mode = normalizeMode(strategy?.executionMode) || 'PAPER';

    // Prepare first. Guard against duplicate clicks (sync ref + mutation pending).
    if (preparingRef.current || prepareOrder.isPending) return;
    const rowId = symbol.backendId;
    if (!rowId) { toast.error('Watchlist row is not yet saved on the server.'); return; }
    preparingRef.current = true;
    expiredRef.current = false;
    toast.info(`Preparing ${mode} order…`);
    prepareOrder.mutate(
      { rowId, payload: { action: side } },
      {
        onSuccess: (prepared) => {
          preparingRef.current = false;
          // Open the modal only now, with the prepared (frozen) values. Token lives only in this ctx.
          setSignalCtx({ strategy, symbol, side, mode: normalizeMode(prepared?.mode) || mode, prepared });
        },
        onError: (e) => {
          preparingRef.current = false;
          // Preparation errors do NOT open the modal and never fall back to /signal.
          toast.error(`Could not prepare ${mode} ${side} — ${extractOrderErrorMessage(e)}`);
        },
      },
    );
  };
  const closeSignal = () => {
    // Clearing signalCtx drops the prepared token/intent from memory (never persisted/logged).
    setSignalCtx(null);
    setSignalError(null);
    setSignalSubmitting(false);
    preparingRef.current = false;
    confirmingRef.current = false;
  };
  // 60s expiry (driven by the modal off backend expires_at). Clear prepared state BEFORE closing so a
  // late click cannot reach /confirm.
  const onSignalExpire = () => {
    if (expiredRef.current) return;
    expiredRef.current = true;
    closeSignal();
    toast.error('Order confirmation expired after 60 seconds. Please prepare the order again.');
  };
  // Drop a row's immediate Last Signal/Trigger overlay once the backend-persisted values (F1) have
  // caught up — the card calls this after a watchlist refetch returns non-null persisted fields.
  const clearSignalOverride = (deploymentId, rowId) => {
    const key = `${deploymentId}:${rowId}`;
    setSignalOverrides((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Shared success/result handling for PAPER/LIVE /confirm. HTTP 200 ≠ fill — classify by
  // mode/confirmation_status (a LIVE broker rejection can arrive as 200). The prepared intent is
  // consumed once dispatched, so a rejection/uncertain result closes the modal (no retry on a spent intent).
  const handleSignalResult = (res, { strategy, symbol, side, mode }) => {
    const c = classifyManualSignalResult(res, { fallbackMode: strategy.executionMode, side });
    if (c.level === 'error') {
      toast.error(c.text);
      closeSignal();                                // prepared intent spent or rejected before durable fill
      return;
    }
    if (c.level === 'warning') {
      toast.warn(c.text);
      closeSignal();                                // uncertain → Orders is the source of truth
      return;
    }
    // success — overlay ONLY for a confirmed PAPER local fill (F2). LIVE never sets a local overlay.
    if (c.paperFill) {
      setSignalOverrides((prev) => {
        const key = `${strategy.deploymentId}:${symbol.id}`;
        const prevOv = prev[key] || {};
        const raw = res?.fill_price;
        const lastTrigger = (raw != null && raw !== '')
          ? raw
          : (symbol.ltp != null ? symbol.ltp : (prevOv.lastTrigger ?? null));
        return { ...prev, [key]: { lastSignal: side, lastTrigger } };
      });
    }
    toast.success(c.text);
    closeSignal();
  };

  // Confirm. PAPER/LIVE → /confirm { client_request_id, prepared_token, trigger_price }.
  // The backend derives Manual Master fan-out from persisted deployment identity.
  // editedTrigger is the modal's possibly edited prepared Trigger Price.
  const confirmSignal = (editedTrigger) => {
    if (!signalCtx) return;
    const { strategy, symbol, side, mode, prepared } = signalCtx;
    const rowId = symbol.backendId;
    if (!rowId) { setSignalError('Watchlist row is not yet saved on the server.'); return; }
    if (confirmingRef.current || confirmOrder.isPending) return; // duplicate guard

    confirmingRef.current = true;
    setSignalError(null);
    setSignalSubmitting(true);
    confirmOrder.mutate(
      {
        rowId,
        deploymentId: strategy.deploymentId,
        payload: {
          client_request_id: prepared.intent_id,
          prepared_token: prepared.prepared_token,
          trigger_price: String(editedTrigger ?? prepared.trigger_price),
        },
      },
      {
        onSuccess: (res) => { confirmingRef.current = false; handleSignalResult(res, { strategy, symbol, side, mode }); },
        onError: (e) => {
          confirmingRef.current = false;
          const reason = extractOrderErrorMessage(e);
          // Tick/price validation rejected BEFORE dispatch → token still valid → keep modal open to correct.
          const correctable = /tick|positive number|price or tick size/i.test(reason);
          if (correctable) {
            setSignalError(reason);
            setSignalSubmitting(false);
            return;
          }
          const expired = /PREPARED_ORDER_EXPIRED/i.test(reason);
          const sym = prepared?.canonical_symbol || symbol.symbol || symbol.displaySymbol || '';
          closeSignal();
          toast.error(expired
            ? 'Order confirmation expired. Please prepare the order again.'
            : `${side} rejected — ${sym} — ${reason}`);
        },
      },
    );
  };

  const confirmExit = () => { setExitCtx(null); toast.success('Mock exit request generated.'); };
  const confirmCancelOrder = () => { setCancelCtx(null); toast.success('Mock cancel request generated.'); };
  // Positions are not persisted this phase (empty) — no-op stub for the card prop.
  const editPositionRisk = () => {};

  // Add Symbol → persist a new watchlist row by deployment_id (backend).
  // row = Symbol Search API result: { symbol (canonical), display_name (friendly), … }
  const addSymbol = (strategy, row) => {
    const canonical = row?.symbol;
    if (!canonical) return;
    const mt = strategy.manual_trading || {};
    const isMargin = mt.qty_mode === 'BY_MARGIN';
    const payload = {
      symbol: canonical,
      display_symbol: row.display_name || canonical,
      exchange: mt.exchange,
      segment: mt.segment,
      qty_value: isMargin ? null : '1',
      margin_value: isMargin ? '' : null,
      order_type: 'Market',
      mpp: '1',
      target: '0',
      stop_loss: '0',
      risk_unit: strategy.selectedRiskUnit || 'PERCENT', // canonical unit selected on the card
      status: 'Ready',
    };
    createWlRow.mutate(
      { deploymentId: strategy.deploymentId, payload },
      {
        onSuccess: () => toast.success(`${payload.display_symbol} added to watchlist.`),
        onError: (e) => {
          if (e?.status === 409) toast.warn('This symbol is already in the watchlist.');
          else toast.error(e?.message || 'Failed to add symbol.');
        },
      },
    );
  };

  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader
        title="Manual Trading"
        subtitle="Generate manual BUY / SELL signals from deployed Manual Strategies"
        actions={
          <button onClick={refresh} className="btn-ghost flex items-center gap-1.5 text-xs">
            <RefreshCw size={12} /> Refresh
          </button>
        }
      />

      {/* ── Strategy cards ── */}
      {depQ.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-txt-muted">
          <Spinner /> <span className="text-xs font-mono">Loading deployed manual strategies…</span>
        </div>
      ) : depQ.isError ? (
        <ErrorState
          message={depQ.error?.message || 'Failed to load deployed manual strategies.'}
          onRetry={() => depQ.refetch()}
        />
      ) : visibleManualDeployments.length === 0 ? (
        <EmptyState
          title={allManualDeployments.length > 0 ? 'Mapped Manual strategies are managed from Strategies' : 'No deployed Manual Strategies found'}
          description={allManualDeployments.length > 0
            ? 'Mapped Manual child strategies are managed from the Strategies page. Copied orders and positions appear in the Orders and Positions pages.'
            : 'Create a Manual Strategy and deploy it to manage it here.'}
        />
      ) : (
        <div className="space-y-4">
          {visibleManualDeployments.map((s) => (
            <ManualStrategyCard
              key={s.deploymentId}
              strategy={s}
              collapsed={!!collapsed[s.deploymentId]}
              onToggle={() => toggleCollapse(s.deploymentId)}
              onBuy={(strategy, symbol) => openSignal(strategy, symbol, 'BUY')}
              onSell={(strategy, symbol) => openSignal(strategy, symbol, 'SELL')}
              onExit={(strategy, position) => setExitCtx({ strategy, position })}
              onAddSymbol={(strategy, riskUnit) => setAddCtx({ ...strategy, selectedRiskUnit: riskUnit })}
              onAddSymbolSelect={addSymbol}
              onCancelOrder={(strategy, order) => setCancelCtx({ strategy, order })}
              onEditPositionRisk={editPositionRisk}
              signalOverrides={signalOverrides}
              onClearSignalOverride={clearSignalOverride}
              canManageFanout={canManageFanout}
            />
          ))}
        </div>
      )}

      {/* ── Modals (mounted only when open) ── */}
      {signalCtx && (
        <ConfirmManualSignalModal
          open
          side={signalCtx.side}
          ctx={signalCtx}
          submitting={signalSubmitting}
          error={signalError}
          onConfirm={confirmSignal}
          onCancel={closeSignal}
          onExpire={onSignalExpire}
          canUseFanout={canExecuteFanout
            && signalCtx.strategy.strategyModeKnown
            && String(signalCtx.strategy.strategyMode || '').toUpperCase() === 'MASTER_MANUAL'
            && String(signalCtx.strategy.deploymentRole || '').toUpperCase() === 'MASTER_PARENT'}
        />
      )}
      {exitCtx && (
        <ExitPositionModal ctx={exitCtx} onConfirm={confirmExit} onCancel={() => setExitCtx(null)} />
      )}
      {addCtx && (
        <SymbolPickerModal
          open
          selectedSymbol={null}
          lockedExchange={addCtx.manual_trading?.exchange}
          lockedSegment={addCtx.manual_trading?.segment}
          manualTradingConfig={addCtx.manual_trading}
          excludeDirectOptionContracts={String(addCtx.manual_trading?.trade_in || '').toUpperCase() === 'OPTION'}
          scopeHint={`${addCtx.manual_trading?.exchange} · ${friendlySegment(addCtx.manual_trading?.segment)}`}
          onSelect={(row) => addSymbol(addCtx, row)}
          onClose={() => setAddCtx(null)}
        />
      )}
      {cancelCtx && (
        <CancelOrderModal
          ctx={cancelCtx}
          onConfirm={confirmCancelOrder}
          onCancel={() => setCancelCtx(null)}
        />
      )}
    </div>
  );
}
